import React, { useCallback, useMemo, useRef, useState } from "react";

import { Box, Skeleton, Stack } from "@mui/material";
import { useModals, usePrompt } from "hooks";
import { pickBy } from "lodash";
import { MaterialReactTable, MRT_ColumnDef, useMaterialReactTable } from "material-react-table";
import { IApiServiceError, useReportsActions, useReportsQueriesSelector } from "store";
import {
  CashflowGraph,
  ReportCashflowPreset,
  ReportPredictionTable,
  ReportPredictionTableCell,
  ReportPredictionTableRow
} from "store/types";

import { ColumnCell } from "./ColumnCell";
import { FirstColumnCell } from "./FirstColumnCell";
import { OverwriteSettings } from "./OverwriteSettings";
import { SettingCell } from "./SettingCell";
import { SettingForm } from "./SettingForm";

export const FIRST_COLUMN_WIDTH = 200;
export const MEASURE_COLUMN_WIDTH = 110;

export type UpdateSetting = (data: {
  row: Pick<ReportPredictionTableRow, "id" | "type" | "name">;
  anchorEl: HTMLButtonElement;
}) => Promise<void>;

export type UpdateSettingCell = (data: {
  id: number;
  type: ReportPredictionTableRow["type"];
  columnKey: string;
  cellData: ReportPredictionTableCell;
  anchorEl: HTMLButtonElement;
  dealValues: Array<{ id: number; value: number; user_id: number | null }>;
  assetClassName: string;
}) => Promise<void>;

const LoadingSkeleton = () => {
  const rowCount = 8; // Number of skeleton rows to show

  return (
    <Stack spacing={1} sx={{ p: 2 }}>
      {/* Header skeleton */}
      <Skeleton variant="rectangular" height={32} />

      {/* Rows skeletons */}
      {Array(rowCount)
        .fill(0)
        .map((_, rowIndex) => (
          <Skeleton key={`row-${rowIndex}`} variant="rectangular" height={32} />
        ))}
    </Stack>
  );
};

interface Props {
  getGraph: (set_id: number) => Promise<CashflowGraph>;
  currentSetId: number | null;
  presets: ReportCashflowPreset[];
  predictionTableData: ReportPredictionTable | null;
  predictionTableLoading: boolean;
  setPredictionTableData: React.Dispatch<React.SetStateAction<ReportPredictionTable | null>>;
  isDefaultSet: boolean;
}

export const PredictionTable: React.FC<Props> = ({
  getGraph,
  currentSetId,
  presets,
  predictionTableData,
  predictionTableLoading,
  setPredictionTableData,
  isDefaultSet
}) => {
  const { currency, exchangeMethod, dateRange, filters, use_accounting_date } = useReportsQueriesSelector();
  const {
    getCashflowTableAction,
    saveCashflowSettingsCellAction,
    resetCashflowSettingsCellAction,
    saveCashflowSettingsAction,
    resetCashflowSettingsAction
  } = useReportsActions();

  const { errorModal } = useModals();
  const openPrompt = usePrompt();

  const [cellUpdatingKey, setCellUpdatingKey] = useState<string | null>(null);
  const [rowUpdatingId, setRowUpdatingId] = useState<number | null>(null);
  const menuRef = useRef<HTMLButtonElement>(null);

  // It should be called after specific row (setting) or cell (settingCell) is updated
  // By updating the a top row, all children will be updated too
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const updateTableParentRow = async (assetClassName: string) => {
    if (!currentSetId) return;

    try {
      const { rows } = await getCashflowTableAction({
        set_id: currentSetId,
        currency,
        dateRange,
        exchangeDate: exchangeMethod,
        use_accounting_date,
        // Refetch all table instead of specific row to calculate "Total"
        filters: pickBy(filters, (value) => value.length > 0)
        // filters: {
        //   ...pickBy(filters, (value) => value.length > 0),
        //   "Asset Class": [assetClassName]
        // }
      });

      // Update all matching rows from the response
      setPredictionTableData((prevState) => {
        if (!prevState) return prevState;

        const updatedRows = prevState.rows.map((existingRow) => {
          const matchingRow = rows.find((newRow) => newRow.id === existingRow.id);
          return matchingRow || existingRow;
        });

        return {
          ...prevState,
          rows: updatedRows
        };
      });

      getGraph(currentSetId);
    } catch (_e) {
      errorModal({ msg: "Failed to update the data" });
    }
  };

  const handleResetSetting = useCallback(
    async ({
      type,
      id,
      assetClassName
    }: {
      type: ReportPredictionTableRow["type"];
      id: number;
      assetClassName: string;
    }) => {
      if (!currentSetId) return;
      try {
        setRowUpdatingId(id);
        await resetCashflowSettingsAction({ set_id: currentSetId, [`${type}_id`]: id });
        await updateTableParentRow(assetClassName);
      } catch (e) {
        errorModal({ msg: (e as IApiServiceError).message });
      } finally {
        setRowUpdatingId(null);
      }
    },
    [currentSetId, updateTableParentRow]
  );

  const handleResetCellSetting = useCallback(
    async (data: { id: number; type: ReportPredictionTableRow["type"]; assetClassName: string; columnKey: string }) => {
      if (!currentSetId) return;

      const { id, type, assetClassName, columnKey } = data;

      const [year, measure] = columnKey.split("|");
      try {
        setCellUpdatingKey(`${columnKey}_${id}`);
        await resetCashflowSettingsCellAction({
          set_id: currentSetId,
          year: Number(year),
          type: measure,
          [`${type}_id`]: id
        });
        await updateTableParentRow(assetClassName);
      } catch (e) {
        errorModal({ msg: (e as IApiServiceError).message });
      } finally {
        setCellUpdatingKey(null);
      }
    },
    [currentSetId, updateTableParentRow]
  );

  const updateSettingCell = useCallback<UpdateSettingCell>(
    async (data) => {
      if (!predictionTableData || !currentSetId) return;

      const { id, type, columnKey, dealValues, assetClassName, cellData } = data;

      const res = await openPrompt({
        Component: SettingCell,
        props: {
          cellData
        }
      });

      if (!res) return;

      const { value: newValue, isReset } = res;

      // If reset was pressed call reset and stop flow, handleReset will complete the flow.
      if (isReset) {
        return handleResetCellSetting({ id, type, assetClassName, columnKey });
      }

      const [year, measure] = columnKey.split("|");
      let updateRow = true;

      try {
        setCellUpdatingKey(`${columnKey}_${id}`);
        const { parameters_conflict } = await saveCashflowSettingsCellAction({
          set_id: currentSetId,
          year: Number(year),
          type: measure,
          value: newValue,
          deal_values: dealValues,
          [`${type}_id`]: id
        });

        if (parameters_conflict) {
          const override = await openPrompt({ Component: OverwriteSettings });

          if (override) {
            await saveCashflowSettingsCellAction({
              set_id: currentSetId,
              year: Number(year),
              type: measure,
              value: newValue,
              deal_values: dealValues,
              [`${type}_id`]: id,
              override
            });
          } else {
            updateRow = false; // Do not update the row if the user does not want to override the settings
          }
        }

        if (updateRow) {
          await updateTableParentRow(assetClassName);
        }
      } catch (e) {
        errorModal({ msg: (e as IApiServiceError).message });
      } finally {
        setCellUpdatingKey(null);
      }
    },
    [errorModal, handleResetCellSetting, currentSetId, updateTableParentRow]
  );

  const updateSetting = useCallback<UpdateSetting>(
    async ({ row, anchorEl }) => {
      if (!currentSetId) return;
      if (!row.id) return;

      let updateRow = true;
      const rowTypeIdKey = `${row.type}_id`;

      try {
        setRowUpdatingId(row.id);

        const newSettings = await openPrompt({
          Component: SettingForm,
          props: {
            id: row.id,
            settingType: row.type,
            set_id: currentSetId,
            presets
          },
          componentType: "popover",
          anchorEl
        });

        if (!newSettings) return;

        const { preset, ...updatedSettingsValues } = newSettings;

        // Update settings
        const { parameters_conflict } = await saveCashflowSettingsAction({
          set_id: currentSetId,
          [rowTypeIdKey]: row.id, // index of settings what it belongs to: asset_class, sub_asset_class or deal
          preset_id: preset?.id || null,
          ...updatedSettingsValues
        });

        if (parameters_conflict) {
          const override = await openPrompt({ Component: OverwriteSettings });

          if (override) {
            await saveCashflowSettingsAction({
              set_id: currentSetId,
              [rowTypeIdKey]: row.id, // index of settings what it belongs to: asset_class, sub_asset_class or deal
              preset_id: preset?.id || null,
              override,
              ...updatedSettingsValues
            });
          } else {
            updateRow = false; // Do not update the row if the user does not want to override the settings
          }
        }

        if (updateRow) {
          await updateTableParentRow(row.name);
        }
      } catch (e) {
        errorModal({ msg: (e as IApiServiceError).message });
      } finally {
        setRowUpdatingId(null);
      }
    },
    [presets, currentSetId, updateTableParentRow]
  );

  // Create columns for MRT
  const columns: MRT_ColumnDef<ReportPredictionTableRow>[] = useMemo(() => {
    if (!predictionTableData) return [];
    const baseColumns: MRT_ColumnDef<ReportPredictionTableRow>[] = [
      {
        header: "Deal", // First row header'
        accessorKey: "name",
        id: "deal_title_column",
        columns: [
          {
            accessorKey: "name",
            header: "", // Second row header
            size: FIRST_COLUMN_WIDTH,
            minSize: FIRST_COLUMN_WIDTH,
            maxSize: FIRST_COLUMN_WIDTH,
            enableSorting: false,
            enableColumnFilter: false,
            enableGlobalFilter: false,
            enableHiding: false,
            enableResizing: false,
            enablePinning: true,
            Cell: (props) => (
              <FirstColumnCell
                {...props}
                rowUpdatingId={rowUpdatingId}
                menuRef={menuRef}
                cellUpdatingKey={cellUpdatingKey}
                updateSetting={updateSetting}
                onResetSetting={handleResetSetting}
                isDefaultSet={isDefaultSet}
              />
            )
          }
        ]
      }
    ];

    // Group columns by year to create nested structure
    const yearGroups = predictionTableData.columns.reduce(
      (acc, yearMeasures) => {
        const year = yearMeasures[0].key.split("|")[0]; // Extract year from key
        if (!acc[year]) {
          acc[year] = [];
        }
        acc[year].push(...yearMeasures);
        return acc;
      },
      {} as Record<string, Array<{ key: string; title: string }>>
    );

    // Create nested columns for each year
    Object.entries(yearGroups).forEach(([year, measures]) => {
      const yearColumn: MRT_ColumnDef<ReportPredictionTableRow> = {
        header: year, // First row header (time period)
        columns: measures.map((measure) => ({
          accessorKey: measure.key,
          header: measure.title, // Second row header (measure)
          size: MEASURE_COLUMN_WIDTH,
          minSize: MEASURE_COLUMN_WIDTH,
          maxSize: MEASURE_COLUMN_WIDTH,
          Cell: (props) => (
            <ColumnCell
              {...props}
              cellUpdatingKey={cellUpdatingKey}
              rowUpdatingId={rowUpdatingId}
              updateSettingCell={updateSettingCell}
            />
          ),
          enableSorting: false,
          enableColumnFilter: false,
          enableGlobalFilter: false,
          enableHiding: false,
          enableResizing: false
        }))
      };
      baseColumns.push(yearColumn);
    });

    return baseColumns;
  }, [
    predictionTableData,
    cellUpdatingKey,
    rowUpdatingId,
    updateSettingCell,
    updateSetting,
    handleResetSetting,
    menuRef,
    isDefaultSet
  ]);

  const table = useMaterialReactTable({
    columns,
    data: predictionTableData?.rows || [],

    getSubRows: (row) => row.children,
    enableExpanding: true,
    enableExpandAll: false,
    enableColumnActions: false,
    enableRowVirtualization: true,
    // Hide MRT's built-in expand column
    displayColumnDefOptions: {
      "mrt-row-expand": {
        muiTableHeadCellProps: {
          sx: { display: "none" }
        },
        muiTableBodyCellProps: {
          sx: { display: "none" }
        }
      }
    },

    // Enable sticky features
    enableStickyHeader: true,
    enableColumnPinning: true,

    // Pin the first column (Deal column)
    initialState: {
      columnPinning: {
        left: ["name"] // Pin the 'name' column to the left
      }
    },

    enableColumnFilters: false,
    enablePagination: false,
    enableSorting: false,
    enableBottomToolbar: false,
    enableTopToolbar: false,
    enableTableFooter: false,
    enableTableHead: true,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableGlobalFilter: false,
    enableRowSelection: false,

    muiTableHeadRowProps: ({ headerGroup }) => ({
      sx: {
        backgroundColor: "#F6F8FC",
        position: "sticky",
        top: headerGroup.depth * 40
      }
    }),

    // Custom styling for sticky columns
    muiTableHeadCellProps: ({ column }) => {
      return {
        align: "center",
        verticalAlign: "middle",
        sx: {
          borderBottom: "1px solid #DBE8F5",
          borderRight: "1px solid #DBE8F5",
          fontWeight: 500,
          fontSize: "14px",
          lineHeight: "16px",
          padding: "4px 12px",
          height: "40px",
          position: "sticky",
          top: column.depth * 40, // ← Key fix: different top values
          left: column.getIsPinned() ? 0 : undefined, // ← Add sticky left positioning
          zIndex: column.getIsPinned() ? 15 : 10, // ← Higher z-index for pinned columns
          verticalAlign: "middle",
          textAlign: column.id === "deal_title_column" ? "left" : "center",
          // Force left alignment on all inner wrappers
          "& *": {
            textAlign: column.id === "deal_title_column" ? "left !important" : "center",
            justifyContent: column.id === "deal_title_column" ? "flex-start !important" : "center"
          },
          "&:last-child": {
            borderRight: "none"
          },
          // Ensure all header cells have proper background with multiple selectors
          backgroundColor: "#F6F8FC !important",
          "&, & *": {
            backgroundColor: "#F6F8FC !important"
          },
          // Remove box-shadow for sticky columns
          ...(column.getIsPinned() && {
            boxShadow: "none !important",
            "&:before": {
              display: "none !important"
            },
            "&:after": {
              display: "none !important"
            }
          })
        }
      };
    },

    muiTableBodyCellProps: ({ column }) => ({
      sx: {
        borderBottom: "1px solid #DBE8F5",
        borderRight: "1px solid #DBE8F5",
        // Solid white background for all cells
        bgcolor: "#FFFFFF",
        padding: 0,
        height: 32,
        fontSize: "13px",
        overflow: "hidden",
        position: column.getIsPinned() ? "sticky" : "relative",
        left: column.getIsPinned() ? 0 : undefined,
        zIndex: column.getIsPinned() ? 5 : 1,
        // Ensure solid background with no transparency
        backgroundColor: "#FFFFFF !important",
        // Override MRT's pinned column pseudo-element styling
        "&:before": {
          display: "none !important"
        },
        "&:after": {
          display: "none !important"
        },
        "&:last-child": {
          borderRight: "none"
        }
      }
    }),

    muiTableBodyRowProps: {
      hover: false
    },

    muiTableContainerProps: {
      sx: {
        maxHeight: "calc(100vh - 300px)", // Dynamic height - shrinks with content
        overflow: "auto"
      }
    },

    muiTableProps: {
      sx: {
        tableLayout: "fixed",
        borderCollapse: "separate",
        borderSpacing: 0,
        // Global override for pinned columns
        "& td[data-pinned='true']": {
          backgroundColor: "#FFFFFF !important",
          opacity: "1 !important",
          "&:before": {
            display: "none !important"
          },
          "&:after": {
            display: "none !important"
          }
        },
        "& .MuiTableCell-root[data-pinned='true']": {
          backgroundColor: "#FFFFFF !important",
          opacity: "1 !important"
        },
        // Force header background color
        "& .MuiTableHead-root .MuiTableCell-root": {
          backgroundColor: "#F6F8FC !important",
          "&, & *": {
            backgroundColor: "#F6F8FC !important"
          }
        }
      }
    }
  });

  if (predictionTableLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <Box
      sx={{
        boxShadow: "none",
        flexGrow: 1,
        overflow: "hidden",
        border: "1px solid #DBE8F5",
        borderRadius: "8px",
        position: "relative",
        isolation: "isolate",
        minHeight: 300,
        backgroundColor: "white"
      }}
    >
      <MaterialReactTable table={table} />
    </Box>
  );
};
